import {
  resolveExerciseStateIdFromRow,
  TE_STATE_FINALIZED,
} from "./coachExerciseNavigation";
import {
  resolveCompletedSequenceOrder,
  sortedExerciseSequences,
} from "./liveTrainingSequenceAutoeval";
import { isTeSequences } from "./teExecutionType";

/** Unique sequence_order values, preserving first-seen order. */
export function uniqueSequenceOrders(exercise) {
  const orders = [];
  const seen = new Set();
  for (const seq of sortedExerciseSequences(exercise)) {
    const order = Number(seq?.sequence_order);
    if (!Number.isFinite(order) || order <= 0 || seen.has(order)) continue;
    seen.add(order);
    orders.push(order);
  }
  return orders;
}

function currentSequenceOrder(exercise, currentSequenceIndexByTeId = {}) {
  const cs = exercise?.currentSequence;
  if (cs && typeof cs === "object" && cs.sequence_order != null) {
    const n = Number(cs.sequence_order);
    if (Number.isFinite(n) && n > 0) return n;
  }
  if (typeof cs === "number" && Number.isFinite(cs) && cs > 0) return cs;
  const orders = uniqueSequenceOrders(exercise);
  const idx = currentSequenceIndexByTeId?.[String(exercise?.te_id)];
  if (idx != null && Number.isFinite(Number(idx)) && orders[idx] != null) {
    return orders[idx];
  }
  return null;
}

/**
 * Sequence orders the demo coach may open for results + processed videos.
 * Finalized TEs: every sequence (or the whole exercise when it is not sequenced).
 * In-progress TE_SEQUENCES: only blocks that have already finished.
 */
export function completedSequenceOrdersForReview(
  exercise,
  {
    realtimeByTeId = {},
    currentSequenceIndexByTeId = {},
    awaitingManualSequenceStart = false,
    awaitingAutoSequenceStart = false,
    lastSequenceCompleted = false,
    isCurrentExercise = false,
  } = {}
) {
  const sid = resolveExerciseStateIdFromRow(exercise, realtimeByTeId);
  const orders = uniqueSequenceOrders(exercise);
  const sequenced = isTeSequences(exercise) && orders.length > 0;

  if (sid === TE_STATE_FINALIZED) {
    return sequenced ? orders : [null];
  }

  if (!sequenced) return [];

  const awaitingGap = !!(
    awaitingManualSequenceStart || awaitingAutoSequenceStart
  );
  const completed = isCurrentExercise
    ? resolveCompletedSequenceOrder(exercise, {
        awaitingManualSequenceStart,
        awaitingAutoSequenceStart,
        lastSequenceCompleted,
      })
    : null;
  const current = currentSequenceOrder(exercise, currentSequenceIndexByTeId);

  return orders.filter((order) => {
    if (isCurrentExercise && awaitingGap && completed != null) {
      return order <= completed;
    }
    if (current != null) return order < current;
    return false;
  });
}

/**
 * Clickable review chips for the live demo coach dashboard.
 * Does not change the live control target — parent should open ExerciseResultDialog.
 */
export function buildDemoFinalizedReviewItems(
  exercises = [],
  {
    realtimeByTeId = {},
    currentTeId = null,
    currentSequenceIndexByTeId = {},
    awaitingManualSequenceStart = false,
    awaitingAutoSequenceStart = false,
    lastSequenceCompleted = false,
  } = {}
) {
  const list = [...(exercises || [])].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const items = [];
  for (const exercise of list) {
    if (!exercise?.te_id) continue;
    const isCurrent =
      currentTeId != null && String(exercise.te_id) === String(currentTeId);
    const orders = completedSequenceOrdersForReview(exercise, {
      realtimeByTeId,
      currentSequenceIndexByTeId,
      awaitingManualSequenceStart,
      awaitingAutoSequenceStart,
      lastSequenceCompleted,
      isCurrentExercise: isCurrent,
    });
    for (const sequenceOrder of orders) {
      items.push({
        key:
          sequenceOrder == null
            ? String(exercise.te_id)
            : `${exercise.te_id}:${sequenceOrder}`,
        teId: String(exercise.te_id),
        teOrder: exercise.te_order ?? null,
        sequenceOrder,
        exercise,
      });
    }
  }
  return items;
}
